import { IPromise } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { IRestLayerRequest } from "@gtmhub/core";
import { IStateInit } from "@gtmhub/core/routing";
import { EmployeeService, IEmployee } from "@gtmhub/employees";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { ICollection, IGtmhubRootScopeService } from "@gtmhub/models";
import { Paginator } from "@gtmhub/teams";

export interface IMultiUserSelectorScope extends IModalScope {
  users: ICollection<IEmployee>;
  selectedUsers: string[];
  usersToExclude: string[];
  countSelectedUsers: number;
  indicators;
  paginator: Paginator;
  querySearch: string;

  toggleUserInSelection(id: string);
  finish();
  onPageChange(page: number): void;
  searchEmployees(employeeName: string): void;
}

export class MultiUserSelectorCtrl implements IStateInit {
  public static $inject = ["$rootScope", "$scope", "UIErrorHandlingService", "EmployeeService", "alreadySelectedUsers", "excludedUsers"];
  constructor(
    private $rootScope: IGtmhubRootScopeService,
    private $scope: IMultiUserSelectorScope,
    private uiErrorHandlingService: UIErrorHandlingService,
    private employeeService: EmployeeService,
    private alreadySelectedUsers: string[],
    private excludedUsers: string[]
  ) {
    this.$scope.indicators = {};
    this.$scope.selectedUsers = [];
    this.$scope.countSelectedUsers = 0;
    this.addAlreadySelected(this.alreadySelectedUsers);
    this.$scope.toggleUserInSelection = this.toggleUserInSelection;
    this.$scope.finish = this.finish;
    this.$scope.onPageChange = this.onPageChange;
    this.$scope.searchEmployees = this.searchEmployees;
    this.$scope.usersToExclude = this.excludedUsers;
  }

  public stateInit(): IPromise<unknown> {
    this.setDefaultPaginator();
    return this.getEmployees();
  }

  private setDefaultPaginator = (): void => {
    this.$scope.paginator = {
      currentPage: 1,
      itemsPerPage: 10,
      paginatorLimit: 5,
    };
  };

  private onPageChange = (page: number): void => {
    this.$scope.paginator.currentPage = page;
    this.getEmployees("loadingUsers", this.$scope.querySearch);
  };

  private searchEmployees = (querySearch: string): void => {
    this.$scope.querySearch = querySearch;
    this.setDefaultPaginator();
    this.getEmployees("search", this.$scope.querySearch);
  };

  private getEmployees = (indicator = "loadingUsers", employeeSearch?: string): IPromise<unknown> => {
    this.$scope.indicators[indicator] = true;

    const queryParams: IRestLayerRequest = {
      skip: (this.$scope.paginator.currentPage - 1) * this.$scope.paginator.itemsPerPage,
      limit: this.$scope.paginator.itemsPerPage,
      fields: "picture,email,firstName,lastName,teamIds,managedTeamIds",
    };

    if (employeeSearch) {
      queryParams.filter = {
        $and: [
          { $or: [{ firstName: { $regex: employeeSearch } }, { lastName: { $regex: employeeSearch } }, { email: { $regex: employeeSearch } }] },
          { isActive: { $ne: false } },
        ],
      };
    } else {
      queryParams.filter = { $and: [{ isActive: { $ne: false } }] };
    }

    if (this.excludedUsers?.length > 0) {
      (queryParams.filter["$and"] as object[]).push({ _id: { $nin: this.excludedUsers } });
    }

    return this.employeeService.getEmployeesV2(queryParams).then(
      (employeesCollection) => {
        this.$scope.indicators[indicator] = false;

        this.$scope.users = employeesCollection;
        this.$scope.paginator.totalPages = Math.ceil(employeesCollection.totalCount / this.$scope.paginator.itemsPerPage);
      },
      (error) => {
        this.uiErrorHandlingService.handleIndicator(error, { indicator, inline: true });
      }
    );
  };

  private addAlreadySelected = (preSelected: string[]): void => {
    preSelected = preSelected ? preSelected : [];

    preSelected.forEach((userId) => {
      this.toggleUserInSelection(userId);
    });
  };

  private toggleUserInSelection = (id: string) => {
    if (this.$scope.selectedUsers.indexOf(id) === -1) {
      this.$scope.selectedUsers.push(id);
    } else {
      const index = this.$scope.selectedUsers.indexOf(id);
      this.$scope.selectedUsers.splice(index, 1);
    }
    this.$scope.countSelectedUsers = this.$scope.selectedUsers.length;
  };

  private finish = () => {
    this.$rootScope.$broadcast("usersSelected", this.$scope.selectedUsers);
    this.$scope.$close();
  };
}
